import React, { useCallback, useContext } from 'react';
import { useVisibleFeatures } from '../entities/Feature/queries';
import { useSessionProvider } from './sessionContext';

interface IFeatureVisibilityContext {
    visibleFeatures: string[];
    isFeatureVisible: (code: string) => boolean;
    isLoading: boolean;
}

const featureVisibilityContext = React.createContext<IFeatureVisibilityContext>({
    visibleFeatures: [],
    isFeatureVisible: () => true,
    isLoading: false,
});

type FeatureVisibilityProviderProps = {
    children: React.ReactNode;
};

export const FeatureVisibilityProvider: React.FC<FeatureVisibilityProviderProps> = ({ children }) => {
    const { currentUser } = useSessionProvider();

    const facilityId = currentUser?.selectedHealthcareFacilityID;
    const isAdmin = !!currentUser && !currentUser.patientID && !currentUser.healthcareProfessionalID;
    const isLoggedIn = !!currentUser?.hasProfile;
    const shouldFetch = isLoggedIn && !isAdmin;

    const { data: visibleFeatures, isLoading } = useVisibleFeatures(facilityId, shouldFetch);

    const isFeatureVisible = useCallback(
        (code: string) => {
            if (!isLoggedIn || isAdmin) return true;
            if (isLoading || !visibleFeatures) return false;
            return visibleFeatures.includes(code);
        },
        [isLoggedIn, isAdmin, isLoading, visibleFeatures],
    );

    return (
        <featureVisibilityContext.Provider
            value={{
                visibleFeatures: visibleFeatures ?? [],
                isFeatureVisible,
                isLoading,
            }}
        >
            {children}
        </featureVisibilityContext.Provider>
    );
};

export function useFeatureVisibility() {
    return useContext(featureVisibilityContext);
}
